import { useGetActivityHistory } from '@practium/features/api';
import { useDebounce } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 5;

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Categoria',
		options: [
			{ value: 'category:Análises', label: 'Análises' },
			{ value: 'category:Defensivos', label: 'Defensivos' },
			{ value: 'category:Fertilizantes', label: 'Fertilizantes' },
			{ value: 'category:Parcelamentos', label: 'Parcelamento' },
			{ value: 'category:Suporte', label: 'Suporte' },
			{ value: 'category:Setores', label: 'Setores' },
			{ value: 'category:Áreas', label: 'Áreas' },
		],
	},
	{
		label: 'Período',
		selectionMode: 'single',
		options: [
			{ value: 'periodo:15', label: 'Últimos 15 dias' },
			{ value: 'periodo:30', label: 'Últimos 30 dias' },
			{ value: 'periodo:custom', label: 'Personalizado' },
		],
	},
];

export const useHistoryFilter = () => {
	const [searchQuery, setSearchQuery] = useState('');
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const debouncedSearch = useDebounce(searchQuery, 400);
	const categoryFilters = useMemo(
		() => filterValues
			.filter((value) => value.startsWith('category:'))
			.map((value) => value.slice('category:'.length)),
		[filterValues]
	);
	const selectedPeriod = filterValues.find((value) => value.startsWith('periodo:'))?.slice('periodo:'.length);
	const period = selectedPeriod === '15' || selectedPeriod === '30' ? `${selectedPeriod}d` : selectedPeriod;
	const hasCustomPeriod = selectedPeriod === 'custom';
	const search = debouncedSearch.trim() || undefined;
	const startDate = hasCustomPeriod && customDateFrom ? `${customDateFrom}T00:00:00-03:00` : undefined;
	const endDate = hasCustomPeriod && customDateTo ? `${customDateTo}T00:00:00-03:00` : undefined;
	const filtersKey = JSON.stringify([search, categoryFilters, period, startDate, endDate]);
	const [pagination, setPagination] = useState({ filtersKey, page: 1 });
	const page = pagination.filtersKey === filtersKey ? pagination.page : 1;
	const setPage = (nextPage: number) => setPagination({ filtersKey, page: nextPage });

	const { data: items, isLoading, isError, refetch } = useGetActivityHistory({
		page,
		limit: PAGE_SIZE,
		search,
		category: categoryFilters.length > 0 ? categoryFilters : undefined,
		period,
		startDate,
		endDate,
	});
	const totalPages = Math.max(1, items?.totalPages ?? 1);

	useEffect(() => {
		setPagination((current) => {
			if (current.filtersKey !== filtersKey) return { filtersKey, page: 1 };
			if (items && current.page > totalPages) return { filtersKey, page: totalPages };
			return current;
		});
	}, [filtersKey, items, totalPages]);

	const filteredItems = items?.docs ?? [];

	return {
		page,
		setPage,
		totalPages,
		isLoading,
		isError,
		refetch,
		searchQuery,
		setSearchQuery,
		filterValues,
		setFilterValues,
		filterGroups: FILTER_GROUPS,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		hasCustomPeriod,
		filteredItems,
	};
};
